import type { VirtualAppointmentOverviewItem } from '../../../services/virtualAppointmentsOverview';
import type { LatenessTier } from '../timeThresholds';
import { isDueDayReached } from '../../virtual-appointments/virtualAppointmentWeeks';
import { daysBetween } from './triggerHint';

export interface LateDistance {
    /** How long ago the trigger was reached, e.g. "6d ago" or "4 aligners ago"; null if unknown. */
    ago: string | null;
    tier: LatenessTier;
}

// A stuck VA is late from its trigger on: yellow at once, orange, then red.
const DAY_STEPS = { moderate: 3, extreme: 7 };
const ALIGNER_STEPS = { moderate: 1, extreme: 2 };

function tierFor(distance: number, steps: typeof DAY_STEPS): LatenessTier {
    if (distance >= steps.extreme) return 'extreme';
    if (distance >= steps.moderate) return 'moderate';
    return 'late';
}

export const LATE_REASON_TEXT: Record<'blocked' | 'orphaned', string> = {
    blocked: "Due, but the patient's status keeps the trigger from opening it.",
    orphaned: 'Open, but the patient is outside the DM and Clinical VA steps, so no team sees it.'
};

// Measured from the trigger itself: days past the due day, or aligners past the target.
export function lateDistance(
    item: VirtualAppointmentOverviewItem,
    todayIso: string
): LateDistance | null {
    if (!item.late) return null;
    // A VA opened by hand before its due day can still be stuck; it just has no distance yet.
    if (item.dueDate !== null && isDueDayReached(item.dueDate, todayIso)) {
        const days = daysBetween(item.dueDate, todayIso);
        return { ago: days === 0 ? 'today' : `${days}d ago`, tier: tierFor(days, DAY_STEPS) };
    }
    const current = item.patient.currentAligner;
    if (item.triggerAligner !== null && current !== null && current >= item.triggerAligner) {
        const past = current - item.triggerAligner;
        const ago = past === 0 ? 'this aligner' : `${past} aligner${past === 1 ? '' : 's'} ago`;
        return { ago, tier: tierFor(past, ALIGNER_STEPS) };
    }
    return { ago: null, tier: 'late' };
}
